/**
 * Purpose: Owner Dashboard page shell — composition only. Data/permissions come
 *          from useOwnerDashboard; panels come from the dashboard module registry
 *          via DashboardModuleRenderer. This file knows no individual module,
 *          exactly like BusinessDetailPage.
 * Inputs: Route params (business slug/id, optional moduleId segment).
 * Outputs: DashboardHeader + nav + active module panel; skeleton while loading;
 *          access screen for non-owners.
 */
import React, { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import useOwnerDashboard from '../hooks/useOwnerDashboard';
import DashboardHeader from '../components/businesses/dashboard/DashboardHeader';
import { DashboardLayout, DashboardModuleRenderer, DashboardSkeleton } from '../components/dashboard';
import { CTAButton } from '../components/ui';
import { getSlugFromParams } from '../utils/urlHelpers';

const StatusScreen = ({ icon, title, message, actionLabel, onAction }) => (
  <PageLayout title="PakPoints">
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <div className="mb-4 text-5xl" aria-hidden>{icon}</div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h2>
        <p className="mb-6 text-gray-600 dark:text-gray-300">{message}</p>
        <CTAButton onClick={onAction}>{actionLabel}</CTAButton>
      </div>
    </div>
  </PageLayout>
);

const BusinessDashboardPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const businessIdentifier = getSlugFromParams(params, 'business');
  const moduleId = params.moduleId || null;

  const { business, loading, error, canManage, modules, navItems, activeId, ctx, reload, applyBusiness } =
    useOwnerDashboard(businessIdentifier, moduleId);

  // The URL owns the active module: nav is linkable, shareable, back-button safe.
  const selectModule = useCallback(
    (id) => navigate(`/businesses/${businessIdentifier}/dashboard/${id}`),
    [navigate, businessIdentifier],
  );

  const handleEditBusiness = useCallback(
    () => navigate(`/businesses/${businessIdentifier}/edit`),
    [navigate, businessIdentifier],
  );

  if (loading) {
    return (
      <PageLayout title="Dashboard - PakPoints">
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
          <DashboardSkeleton />
        </div>
      </PageLayout>
    );
  }

  if (error || !business) {
    return (
      <StatusScreen
        icon="⚠️"
        title="Business Not Found"
        message={error || "The business you're looking for doesn't exist."}
        actionLabel="Back to Businesses"
        onAction={() => navigate('/businesses')}
      />
    );
  }

  // UX gate only — the server re-authorizes every dashboard mutation.
  if (!canManage) {
    return (
      <StatusScreen
        icon="🔒"
        title="Owner access only"
        message="You don't have permission to manage this business."
        actionLabel="View public page"
        onAction={() => navigate(`/businesses/${businessIdentifier}`)}
      />
    );
  }

  return (
    <PageLayout title={`${business.name} Dashboard - PakPoints`}>
      <div className="min-h-screen bg-gray-50 pb-24 dark:bg-gray-950 lg:pb-10">
        <DashboardLayout
          header={<DashboardHeader business={business} />}
          items={navItems}
          activeId={activeId}
          onSelect={selectModule}
        >
          <DashboardModuleRenderer
            modules={modules}
            activeId={activeId}
            ctx={ctx}
            moduleProps={{ onEditBusiness: handleEditBusiness, onRefresh: reload, onApplyBusiness: applyBusiness }}
          />
        </DashboardLayout>
      </div>
    </PageLayout>
  );
};

export default BusinessDashboardPage;
